import React, { useState, useRef, useEffect } from "react"
import types from "prop-types"
import clsx from "clsx"
import InputErrorMessage from "shared/InputErrorMessage"
import { withFormsy } from "formsy-react"
import { encode, decode } from "utils/mentionHelper"

import "./MultiLineMentionableInput.scss"

const MultiLineMentionableInput = ({
  value = "",
  users = [],
  onChange = () => {},
  disabled = false,
  className = "",
  errorMessages = [],
  isPristine = true,
}) => {
  const [showSelector, setShowSelector] = useState(false)
  const [selectorPosition, setSelectorPosition] = useState({ left: 0, top: 0 })
  const [filteredUsers, setFilteredUsers] = useState(users)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [mentionStartPosition, setMentionStartPosition] = useState(null)
  const [hasFocus, setHasFocus] = useState(false)
  const divRef = useRef(null)
  const selectorRef = useRef(null)
  const highlightedUserRef = useRef(null)

  useEffect(() => {
    if (divRef.current && divRef.current.innerHTML !== value) {
      if (divRef.current.innerHTML !== decode(value)) divRef.current.innerHTML = decode(value)
    }
  }, [value])

  useEffect(() => {
    const handleFocus = () => setHasFocus(true)
    const handleBlur = () => {
      setTimeout(() => {
        setHasFocus(false)
      }, 100)
    }

    const divElement = divRef.current
    if (divElement) {
      divElement.addEventListener("focus", handleFocus, true)
      divElement.addEventListener("blur", handleBlur, true)
    }

    return () => {
      if (divElement) {
        divElement.removeEventListener("focus", handleFocus, true)
        divElement.removeEventListener("blur", handleBlur, true)
      }
    }
  }, [])

  useEffect(() => {
    if (!hasFocus) {
      setShowSelector(false)
    }
  }, [hasFocus])

  useEffect(() => {
    if (highlightedUserRef.current) {
      highlightedUserRef.current.scrollIntoView({
        block: "nearest",
      })
    }
  }, [highlightedIndex])

  const handleInput = (e) => {
    const html = e.target.innerHTML

    // this part is to remove unwanted auto generated styles applied by the browser
    const cleanedHtml = html
      .replace(/<font[^>]*>|<\/font>/g, "") // Remove all <font> tags
      .replace(/<b(?:\s[^>]*)?>(.*?)<\/b>/g, (match, p1) => {
        // Check if the <b> tag has attributes
        if (match.startsWith("<b ") && match.includes("=")) {
          // Preserve <b> tags with attributes
          return match
        }
        // Remove <b> tags without attributes
        return p1
      })

    // Set the cleaned HTML back to the contenteditable div
    if (html !== cleanedHtml) {
      e.target.innerHTML = cleanedHtml
      // Move the cursor to the end of the content
      const range = document.createRange()
      const sel = window.getSelection()
      range.selectNodeContents(e.target)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
      return
    }

    const sel = window.getSelection()
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      const offset = range.startOffset
      const node = range.startContainer

      if (node.nodeType === Node.TEXT_NODE) {
        const textBeforeCaret = node.textContent.slice(0, offset)
        const atIndex = textBeforeCaret.lastIndexOf("@")

        if (atIndex !== -1) {
          const caretRect = range.getBoundingClientRect()
          const parentRect = divRef.current.getBoundingClientRect()

          // Calculate the position relative to the parent div
          const left = caretRect.left - parentRect.left
          const top = caretRect.top - parentRect.top

          setSelectorPosition({ left, top })
          setShowSelector(true)

          const searchText = textBeforeCaret.slice(atIndex + 1).toLowerCase()
          const filtered = users.filter((user) => user.fullName.toLowerCase().includes(searchText))
          setFilteredUsers(filtered)

          setMentionStartPosition({
            node,
            offset: atIndex,
          })
        } else {
          setShowSelector(false)
        }
      }
    }

    onChange(encode(html))
  }

  const handleUserSelect = (user) => {
    if (mentionStartPosition) {
      const { node, offset } = mentionStartPosition
      const { textContent } = node

      // Get the part before '@'
      const beforeMention = textContent.slice(0, offset)

      // Get the part after the mention text (remove @ and any following text)
      const afterMention = textContent.slice(offset).replace(/^@\S*/, "")

      // Create mention node
      const mentionText = document.createTextNode(user.fullName)
      const mentionNode = document.createElement("b")
      mentionNode.classList.add("mention")
      mentionNode.setAttribute("user_id", user.id)
      mentionNode.appendChild(mentionText)

      // Replace existing text node with new nodes
      const range = document.createRange()
      range.selectNodeContents(node)
      range.deleteContents()

      const parent = node.parentNode
      parent.insertBefore(document.createTextNode(beforeMention), node)
      parent.insertBefore(mentionNode, node)
      parent.insertBefore(document.createTextNode(` ${afterMention}`), node)

      // Move cursor after mention
      const sel = window.getSelection()
      const newRange = document.createRange()
      newRange.setStartAfter(mentionNode.nextSibling)
      newRange.collapse(true)
      sel.removeAllRanges()
      sel.addRange(newRange)

      setShowSelector(false)
      divRef.current.focus()

      onChange(encode(divRef.current.innerHTML))
    }
  }

  const handleKeyDown = (e) => {
    let sel = window.getSelection()
    if (!sel.rangeCount) return

    let range = sel.getRangeAt(0)
    const node = range.startContainer
    const offset = range.startOffset
    const { nextSibling } = node

    if (e.key === "Tab") {
      setShowSelector(false)
    } else if (e.key === "Enter" && !showSelector) {
      e.preventDefault()

      sel = window.getSelection()
      if (sel.rangeCount > 0) {
        range = sel.getRangeAt(0)
        range.deleteContents()
        const textNode = document.createTextNode("\n\u200B");
        range.insertNode(textNode)
        range = document.createRange()
        range.setStartAfter(textNode)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)

        onChange(encode(divRef.current.innerHTML))
      }
    } else if (showSelector) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev < filteredUsers.length - 1 ? prev + 1 : prev))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
      } else if (e.key === "Enter") {
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < filteredUsers.length) {
          handleUserSelect(filteredUsers[highlightedIndex])
        }
      }
    } else if (e.key === "Backspace") {
      if (node.parentNode.classList.contains("mention") && offset !== 0) {
        e.preventDefault()
        node.parentNode.remove()
        divRef.current.focus()
        onChange(encode(divRef.current.innerHTML))
      }
    } else if (e.key === "Delete") {
      // Ensure caret is exactly before a mention with no spaces
      if (
        nextSibling
        && nextSibling.classList
        && nextSibling.classList.contains("mention")
        && node.nodeType === Node.TEXT_NODE
        && node.textContent.length === offset // Caret is at the end of this text node
      ) {
        e.preventDefault()
        nextSibling.remove()
        divRef.current.focus()
        if (onChange) onChange(encode(divRef.current.innerHTML))
      } else if (
        node.parentNode.classList.contains("mention") && offset !== node.textContent.length
      ) {
        e.preventDefault()
        node.parentNode.remove()
        divRef.current.focus()
        if (onChange) onChange(encode(divRef.current.innerHTML))
      }
    } else {
      const allowedKeys = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ]
      if (node.parentNode.classList.contains("mention") && !allowedKeys.includes(e.key)) {
        e.preventDefault()

        const mentionNode = node.parentNode
        range = document.createRange()
        sel = window.getSelection()

        if (
          mentionNode.nextSibling
          && mentionNode.nextSibling.nodeType === Node.TEXT_NODE
          && mentionNode.nextSibling.textContent.startsWith(" ")
        ) {
          // Move caret after the first whitespace if it exists
          range.setStart(mentionNode.nextSibling, 1)
        } else {
          // No whitespace found, insert one and move the caret after it
          const spaceNode = document.createTextNode(" ")
          mentionNode.parentNode.insertBefore(spaceNode, mentionNode.nextSibling)
          range.setStart(spaceNode, 1)
        }

        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const plainText = e.clipboardData.getData("text/plain").replace(/\r\n/g, "\n")

    const selection = window.getSelection()
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0)
      range.deleteContents()
      range.insertNode(document.createTextNode(plainText))

      // Move cursor to the end
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)

      onChange(encode(divRef.current.innerHTML))
    }
  }

  return (
    <div className="relative">
      <div
        ref={divRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        role="textbox"
        aria-multiline="true"
        tabIndex={0}
        aria-label="mentionable input"
        className={clsx(className, "editor min-h-[40px] bg-white border border-gray-300 p-2 rounded-md outline-none cursor-text whitespace-pre-wrap text-left")}
      />
      {showSelector && filteredUsers.length > 0 && (
        <div
          ref={selectorRef}
          className="selector absolute bg-white border border-gray-300 rounded-md shadow-sm z-[1000]"
          style={{ left: selectorPosition.left, top: selectorPosition.top + 20 }}
          role="listbox"
          aria-label="users"
        >
          {filteredUsers.map((user, index) => (
            <div
              key={user.id}
              ref={index === highlightedIndex ? highlightedUserRef : null}
              onClick={() => handleUserSelect(user)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUserSelect(user)
                }
              }}
              role="option"
              aria-selected={highlightedIndex === index}
              tabIndex={0}
              className={`p-2 cursor-pointer border-b border-gray-200 ${
                highlightedIndex === index ? "bg-gray-100" : "bg-white"
              }`}
            >
              {user.fullName}
            </div>
          ))}
        </div>
      )}
      <InputErrorMessage isPristine={isPristine} errorMessages={errorMessages} />
    </div>
  )
}

MultiLineMentionableInput.propTypes = {
  value: types.string,
  users: types.array,
  disabled: types.bool,
  onChange: types.func,
  className: types.string,
  errorMessages: types.arrayOf(types.string),
  isPristine: types.bool,
}

export const FormsyMultiLineMentionableInput = withFormsy(({ setValue, ...rest }) => (
  <MultiLineMentionableInput
    onChange={(value) => setValue(value.trimStart())}
    {...rest}
  />
))

FormsyMultiLineMentionableInput.displayName = "FormsyMultiLineMentionableInput"

export default MultiLineMentionableInput
